import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

interface BaseProps {
  label: ReactNode;
  /** Texto de ayuda bajo el campo. Se enlaza con aria-describedby. */
  hint?: ReactNode;
  error?: string;
  required?: boolean;
}

interface TextFieldProps extends BaseProps {
  /** Teclado numerico en movil sin las restricciones de type=number. */
  inputMode?: 'decimal' | 'numeric';
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'date' | 'email' | 'tel';
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Unidad o simbolo que se pinta a la derecha del campo. */
  suffix?: string;
  disabled?: boolean;
}

export function TextField({
  inputMode,
  label,
  hint,
  error,
  required,
  value,
  onChange,
  type = 'text',
  placeholder,
  min,
  max,
  step,
  suffix,
  disabled,
}: TextFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className={`field ${error ? 'field--error' : ''}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true"> *</span>}
      </label>
      <div className="field__control">
        <input
          id={id}
          className="input"
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          required={required}
          disabled={disabled}
          aria-describedby={hint || error ? hintId : undefined}
          aria-invalid={error ? true : undefined}
          inputMode={inputMode ?? (type === 'number' ? 'decimal' : undefined)}
        />
        {suffix && <span className="field__suffix">{suffix}</span>}
      </div>
      {(error || hint) && (
        <p className="field__hint" id={hintId}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;  /** Opciones agrupadas (optgroup). Si se pasa, tiene prioridad sobre options. */
  groups?: Array<{ label: string; options: Array<{ value: string; label: string }> }>;
}

export function SelectField({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  options,
  groups,
  disabled,
}: SelectFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className={`field ${error ? 'field--error' : ''}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true"> *</span>}
      </label>
      <select
        id={id}
        className="input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        aria-describedby={hint || error ? hintId : undefined}
      >
        {groups
          ? groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          : options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
      </select>
      {(error || hint) && (
        <p className="field__hint" id={hintId}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
}: CheckboxFieldProps): JSX.Element {
  const id = useId();
  return (
    <div className="field field--check">
      <input
        id={id}
        type="checkbox"
        className="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label className="field__label field__label--inline" htmlFor={id}>
        {label}
      </label>
      {hint && <p className="field__hint">{hint}</p>}
    </div>
  );
}

/** Agrupa campos en filas de dos columnas dentro de un formulario. */
export function FieldRow({ children }: { children: ReactNode }): JSX.Element {
  return <div className="field-row">{children}</div>;
}

/* ------------------------------------------------------------------ */
/* Selector con busqueda                                               */
/* ------------------------------------------------------------------ */

interface SearchSelectProps {
  label: ReactNode;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Cuantas opciones se pintan como maximo. Es un tope de RENDER: con mas
 * coincidencias se le pide al usuario que afine, en vez de escupir una lista
 * de doscientas que tampoco sirve de nada.
 */
const MAX_VISIBLES = 50;

/**
 * Desplegable con busqueda por texto.
 *
 * Un `<select>` con doscientas recetas es inservible: hay que recorrerlo
 * entero para encontrar una (Alex, 1 sep 2026). Aqui se escribe "croq" y
 * quedan las croquetas. Sin dependencias: es un input con una lista filtrada.
 *
 * La lista se ordena alfabeticamente por quien la pasa, no aqui: el orden
 * puede tener sentido propio en otros sitios.
 */
export function SearchSelectField({
  label,
  hint,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: SearchSelectProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const caja = useRef<HTMLDivElement>(null);

  const elegido = options.find((o) => o.value === value);
  const filtradas = useMemo(() => {
    const aguja = texto.trim().toLowerCase();
    if (aguja === '') return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(aguja) || o.value.toLowerCase().includes(aguja),
    );
  }, [options, texto]);
  /*
   * Las que se pintan de verdad. Todo lo que navega el teclado y lo que elige
   * Enter sale de aqui: si el recorrido fuera sobre `filtradas`, con muchas
   * coincidencias se podria acabar eligiendo una opcion que no esta en la
   * lista y que el usuario no ha llegado a ver.
   */
  const mostradas = useMemo(() => filtradas.slice(0, MAX_VISIBLES), [filtradas]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const elegir = (v: string) => {
    onChange(v);
    setTexto('');
    setAbierto(false);
  };

  return (
    <div className="field" ref={caja}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="buscador">
        <input
          id={id}
          className="input"
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={abierto}
          aria-controls={`${id}-lista`}
          disabled={disabled}
          // Cerrado ensenya lo elegido; abierto, lo que se va escribiendo.
          value={abierto ? texto : (elegido?.label ?? '')}
          placeholder={placeholder ?? 'Escribe para buscar…'}
          onFocus={() => {
            setAbierto(true);
            setTexto('');
            setResaltado(0);
          }}
          onChange={(event) => {
            setTexto(event.target.value);
            setAbierto(true);
            setResaltado(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setAbierto(true);
              setResaltado((i) => {
                const paso = event.key === 'ArrowDown' ? 1 : -1;
                // Sobre las que SE VEN, no sobre todas las que coinciden: si
                // no, bajando lo suficiente se resalta —y con Enter se elige—
                // una receta que no esta en pantalla.
                const total = mostradas.length;
                return total === 0 ? 0 : (i + paso + total) % total;
              });
            } else if (event.key === 'Enter' && abierto) {
              event.preventDefault();
              const opcion = mostradas[resaltado];
              if (opcion) elegir(opcion.value);
            } else if (event.key === 'Escape') {
              setAbierto(false);
            }
          }}
          aria-describedby={hint ? hintId : undefined}
        />
        {abierto && (
          <ul className="buscador__lista" id={`${id}-lista`} role="listbox">
            {filtradas.length === 0 && <li className="buscador__vacio">Nada coincide</li>}
            {mostradas.map((option, i) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`buscador__opcion ${i === resaltado ? 'is-resaltada' : ''}`.trim()}
                  onMouseEnter={() => setResaltado(i)}
                  onClick={() => elegir(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
            {filtradas.length > MAX_VISIBLES && (
              <li className="buscador__vacio">
                Y {filtradas.length - MAX_VISIBLES} más: afina la búsqueda.
              </li>
            )}
          </ul>
        )}
      </div>
      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}
